<template>
    <span class="table-filter-pill-root">
        <template v-if="isBooleanFilter">
        <v-tooltip bottom open-delay="300">
            <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
        <button
            v-if="active"
            type="button"
            class="table-filter-pill table-filter-pill--active"
            v-bind="tooltipAttrs"
            v-on="tooltipOn"
            :aria-label="badgeAriaLabel"
        >
            <span class="table-filter-pill__label">
                <v-progress-circular
                    v-if="labelLoading"
                    indeterminate
                    size="14"
                    width="2"
                    color="primary"
                />
                <template v-else>{{ badgeLabel }}</template>
            </span>
            <span
                class="table-filter-pill__remove"
                role="button"
                tabindex="0"
                :aria-label="removeLabel"
                @click.stop="clearFilter"
                @keyup.enter.stop="clearFilter"
            >
                <v-icon x-small>mdi-close</v-icon>
            </span>
        </button>
        <button
            v-else
            type="button"
            class="table-filter-pill table-filter-pill--inactive"
            v-bind="tooltipAttrs"
            v-on="tooltipOn"
            :aria-label="filter.label"
            @click="applyBooleanFilter"
        >
            <span>{{ filter.label }}</span>
        </button>
            </template>
            <span>{{ filterTooltip }}</span>
        </v-tooltip>
    </template>
    <v-menu
        v-else
        v-model="menuOpen"
        :close-on-content-click="false"
        offset-y
        transition="scale-transition"
        max-width="360"
        min-width="280"
    >
        <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
            <v-tooltip bottom open-delay="300">
                <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
            <button
                v-if="active"
                type="button"
                class="table-filter-pill table-filter-pill--active"
                v-bind="{ ...menuAttrs, ...tooltipAttrs }"
                v-on="{ ...menuOn, ...tooltipOn }"
                :aria-label="badgeAriaLabel"
            >
                <span class="table-filter-pill__label">
                    {{ badgeLabel }}
                    <v-progress-circular
                        v-if="labelLoading"
                        class="ml-1"
                        indeterminate
                        size="14"
                        width="2"
                        color="primary"
                    />
                </span>
                <span
                    v-if="filter.clearable !== false"
                    class="table-filter-pill__remove"
                    role="button"
                    tabindex="0"
                    :aria-label="removeLabel"
                    @click.stop="clearFilter"
                    @keyup.enter.stop="clearFilter"
                >
                    <v-icon x-small>mdi-close</v-icon>
                </span>
                <v-icon
                    v-else
                    x-small
                    class="table-filter-pill__chevron"
                >mdi-chevron-down</v-icon>
            </button>
            <button
                v-else
                type="button"
                class="table-filter-pill table-filter-pill--inactive"
                v-bind="{ ...menuAttrs, ...tooltipAttrs }"
                v-on="{ ...menuOn, ...tooltipOn }"
                :aria-label="filter.label"
            >
                <span>{{ filter.label }}</span>
                <v-icon x-small class="table-filter-pill__chevron">mdi-chevron-down</v-icon>
            </button>
                </template>
                <span>{{ filterTooltip }}</span>
            </v-tooltip>
        </template>
        <TableFilterPillPanel
            v-if="usePillPanel && menuOpen"
            :filter="filter"
            :value="filterValue"
            :filters="filters"
            @change="onFilterChange"
            @close="menuOpen = false"
        />
        <v-card v-else class="table-filter-pill__menu pa-3">
            <FiltersItem
                :filter="filter"
                :value="filterValue"
                :filters="filters"
                @change="onFilterChange"
            />
        </v-card>
    </v-menu>
    </span>
</template>

<script>
import FiltersItem from "@/components/filters/filters-item";
import TableFilterPillPanel from "./table-filter-pill-panel";
import {
    createChoiceContext,
    fetchFilterItemLabel,
    getCachedFilterItemLabel,
    needsRemoteLabelResolution,
    normalizeFilterStoredValue,
    setCachedFilterItemLabel,
    supportsPillPanel,
} from "@/utils/filterChoices";
import {
    booleanFilterApplyValue,
    filterHasValue,
    formatFilterBadgeLabel,
    isBooleanFilter as isBooleanFilterType,
    normalizeActiveFilterValue,
    resolveFilterValueLabel,
} from "@/utils/tableFilterLabels";
import {
    activeDateRangePreset,
    dateRangePresetLabel,
} from "@/utils/dateRangePresets";

export default {
    name: "TableFilterPill",
    components: { FiltersItem, TableFilterPillPanel },
    props: {
        filter: {
            type: Object,
            required: true,
        },
        filters: {
            type: Object,
            required: true,
        },
        choices: {
            type: Array,
            default: () => [],
        },
        removeLabel: {
            type: String,
            default: "Remove filter",
        },
        defaultOpen: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            menuOpen: false,
            resolvedLabel: null,
            resolvedValueKey: null,
            labelLoading: false,
            labelRequestId: 0,
        };
    },
    computed: {
        isBooleanFilter() {
            return isBooleanFilterType(this.filter);
        },
        usePillPanel() {
            return supportsPillPanel(this.filter);
        },
        active() {
            return filterHasValue(this.filter, this.filters);
        },
        badgeLabel() {
            if (this.filter.type === "daterange") {
                const preset = activeDateRangePreset(
                    this.filters,
                    this.filter.field,
                );
                if (preset) {
                    return dateRangePresetLabel(this, preset);
                }
            }
            const value = normalizeActiveFilterValue(
                this.filters[this.filter.field],
                this.filter,
            );
            const valueKey = this.getValueKey(value);
            const resolvedLabel =
                (this.resolvedValueKey === valueKey ? this.resolvedLabel : null) ||
                getCachedFilterItemLabel(this.filter, value);
            // Never flash the raw id while a remote label is still loading.
            if (
                !resolvedLabel &&
                (this.labelLoading ||
                    needsRemoteLabelResolution(this.filter, value))
            ) {
                return this.filter.label;
            }
            const formatted = formatFilterBadgeLabel(
                this.filter,
                this.filters,
                this.choices,
                this.$utils,
                resolvedLabel,
                this,
            );
            if (formatted) {
                return formatted;
            }
            if (value !== null && value !== undefined && value !== "") {
                return String(value);
            }
            return this.filter.label;
        },
        badgeAriaLabel() {
            if (this.labelLoading) {
                return this.filter.label;
            }
            return this.badgeLabel;
        },
        filterTooltip() {
            return this.filter.label || this.filter.field;
        },
        filterValue() {
            if (
                this.filter.type === "daterange" ||
                this.filter.type === "numberrange"
            ) {
                const minField = `${this.filter.field}_min`;
                const maxField = `${this.filter.field}_max`;
                return {
                    min: this.filters[minField] ?? null,
                    max: this.filters[maxField] ?? null,
                };
            }
            return normalizeActiveFilterValue(
                this.filters[this.filter.field] ?? null,
                this.filter,
            );
        },
    },
    watch: {
        defaultOpen: {
            handler(open) {
                if (open && !this.isBooleanFilter) {
                    this.menuOpen = true;
                }
            },
            immediate: true,
        },
        menuOpen(open) {
            if (!open) {
                this.$emit("menu-close");
            }
        },
        filterValue: {
            handler() {
                this.syncResolvedLabel();
            },
            immediate: true,
        },
        choices: {
            handler() {
                this.syncResolvedLabel();
            },
            deep: true,
        },
    },
    methods: {
        applyBooleanFilter() {
            this.$emit("change", booleanFilterApplyValue(this.filter));
        },
        normalizeChangePayload(payload) {
            if (
                payload &&
                typeof payload === "object" &&
                !Array.isArray(payload) &&
                Object.prototype.hasOwnProperty.call(payload, "value")
            ) {
                return {
                    value: payload.value,
                    label: payload.label || null,
                };
            }
            return { value: payload, label: null };
        },
        onFilterChange(payload) {
            const { value, label } = this.normalizeChangePayload(payload);
            const normalizedValue = normalizeFilterStoredValue(value, this.filter);
            if (label) {
                this.resolvedLabel = label;
                this.resolvedValueKey = this.getValueKey(normalizedValue);
                this.labelLoading = false;
                setCachedFilterItemLabel(this.filter, normalizedValue, label);
            }
            this.$emit("change", normalizedValue);
            if (this.filter.multiple) {
                return;
            }
            if (
                normalizedValue === null ||
                normalizedValue === undefined ||
                normalizedValue === ""
            ) {
                return;
            }
            if (
                typeof normalizedValue === "object" &&
                !Array.isArray(normalizedValue) &&
                ("min" in normalizedValue || "max" in normalizedValue)
            ) {
                if (normalizedValue.min != null || normalizedValue.max != null) {
                    this.menuOpen = false;
                }
                return;
            }
            this.menuOpen = false;
        },
        clearFilter() {
            this.labelRequestId += 1;
            this.labelLoading = false;
            this.$emit("change", null);
        },
        getValueKey(value) {
            if (value === null || value === undefined) {
                return "";
            }
            if (Array.isArray(value)) {
                return value.map((item) => String(item)).join(",");
            }
            return String(value);
        },
        async syncResolvedLabel() {
            if (!this.active) {
                this.resolvedLabel = null;
                this.resolvedValueKey = null;
                this.labelLoading = false;
                return;
            }

            const value = normalizeFilterStoredValue(
                this.filters[this.filter.field],
                this.filter,
            );
            const valueKey = this.getValueKey(value);

            if (this.resolvedValueKey === valueKey && this.resolvedLabel) {
                this.labelLoading = false;
                return;
            }

            if (this.resolvedValueKey !== valueKey) {
                this.resolvedLabel = null;
            }

            const fromChoices = resolveFilterValueLabel(
                this.filter,
                value,
                this.choices,
                this,
            );
            if (fromChoices && String(fromChoices) !== String(value)) {
                this.resolvedLabel = fromChoices;
                this.resolvedValueKey = valueKey;
                this.labelLoading = false;
                setCachedFilterItemLabel(this.filter, value, fromChoices);
                return;
            }

            const cached = getCachedFilterItemLabel(this.filter, value);
            if (cached) {
                this.resolvedLabel = cached;
                this.resolvedValueKey = valueKey;
                this.labelLoading = false;
                return;
            }

            if (this.resolvedLabel && this.resolvedValueKey === valueKey) {
                this.labelLoading = false;
                return;
            }

            if (!needsRemoteLabelResolution(this.filter, value)) {
                this.resolvedLabel = this.resolveDisplayLabel(value, fromChoices);
                this.resolvedValueKey = valueKey;
                this.labelLoading = false;
                return;
            }

            this.labelLoading = true;
            const requestId = ++this.labelRequestId;
            try {
                const label = await fetchFilterItemLabel(
                    this.filter,
                    value,
                    createChoiceContext(this),
                );
                if (
                    requestId !== this.labelRequestId ||
                    this.getValueKey(
                        normalizeFilterStoredValue(
                            this.filters[this.filter.field],
                            this.filter,
                        ),
                    ) !== valueKey
                ) {
                    return;
                }
                this.resolvedLabel = this.resolveDisplayLabel(
                    value,
                    label || fromChoices,
                );
                this.resolvedValueKey = valueKey;
            } finally {
                if (requestId === this.labelRequestId) {
                    this.labelLoading = false;
                }
            }
        },
        resolveDisplayLabel(value, preferredLabel) {
            if (
                preferredLabel !== null &&
                preferredLabel !== undefined &&
                preferredLabel !== ""
            ) {
                return String(preferredLabel);
            }
            if (value !== null && value !== undefined && value !== "") {
                return String(value);
            }
            return null;
        },
    },
    i18n: {
        messages: {
            en: {
                collapsedFilterValues: "{count} values",
            },
            fr: {
                collapsedFilterValues: "{count} valeurs",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/table-filter-pill";

.table-filter-pill-root {
    display: contents;
}
</style>
